import { ReactElement } from 'react';

import FormHelperText from '@material-ui/core/FormHelperText';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Paper from '@material-ui/core/Paper';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import ErrorIcon from '@material-ui/icons/Error';

import { isCatalogUniquenessError } from '../components/VcsRepositoryNameField/catalogNameUniqueness';

const useErrorListStyles = makeStyles(() =>
  createStyles({
    list: {
      width: '100%',
    },
    text: {
      textWrap: 'wrap',
    },
  }),
);

type SchemaWithProperties = {
  properties?: Record<string, { title?: string } | boolean | undefined>;
};

type FormError = {
  property?: string;
  message?: string;
  stack?: string;
};

export type CatalogUniquenessErrorListTemplateProps = {
  errors: FormError[];
  schema: SchemaWithProperties;
};

export type CatalogUniquenessFieldErrorTemplateProps = {
  errors?: Array<string | ReactElement>;
  idSchema: { $id?: string };
};

function formatErrorMessage(error: FormError, schema: SchemaWithProperties): string {
  if (error.property && error.message) {
    const propertyName = error.property.startsWith('.')
      ? error.property.substring(1)
      : error.property;
    const property = schema.properties?.[propertyName];
    if (typeof property === 'object' && property?.title) {
      return `'${property.title}' ${error.message}`;
    }
    return `'${propertyName}' ${error.message}`;
  }
  return error.stack ?? error.message ?? '';
}

/** Hides catalog uniqueness from the top error list; the name field already shows it. */
export function CatalogUniquenessErrorListTemplate({
  errors,
  schema,
}: CatalogUniquenessErrorListTemplateProps) {
  const classes = useErrorListStyles();
  const remaining = errors.filter(error => !isCatalogUniquenessError(error));

  if (remaining.length === 0) {
    return null;
  }

  return (
    <Paper>
      <List dense className={classes.list}>
        {remaining.map((error, index) => (
          <ListItem key={index}>
            <ListItemIcon>
              <ErrorIcon color="error" />
            </ListItemIcon>
            <ListItemText
              classes={{ primary: classes.text }}
              primary={formatErrorMessage(error, schema)}
            />
          </ListItem>
        ))}
      </List>
    </Paper>
  );
}

/** Hides catalog uniqueness under FieldTemplate; the name field already shows it. */
export function CatalogUniquenessFieldErrorTemplate(
  props: CatalogUniquenessFieldErrorTemplateProps,
) {
  const remaining = (props.errors ?? []).filter(
    error => !isCatalogUniquenessError(error),
  );
  if (remaining.length === 0) {
    return null;
  }

  const id = `${props.idSchema.$id ?? 'root'}__error`;

  return (
    <List dense disablePadding>
      {remaining.map((error, index) => (
        <ListItem key={index} disableGutters>
          <FormHelperText id={id} error>
            {error}
          </FormHelperText>
        </ListItem>
      ))}
    </List>
  );
}
